import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';

export interface Terminal {
  codigo_terminal: number;
  nombre_terminal: string;
  id_sap_oficina_venta_terminal: string | null;
  id_sap_zona_venta_terminal: string | null;
  id_centro_beneficio: string | null;
  pais: string;
}

export interface Equipo {
  codigo_terminal: number;
  codigo_equipo: number;
  nombre_equipo: string;
  id_sap_oficina_venta_equipo: string | null;
  id_sap_zona_venta_equipo: string | null;
}

interface SessionContextType {
  terminal: Terminal | null;
  equipo: Equipo | null;
  setSessionData: (terminal: Terminal, equipo: Equipo) => void;
  clearSession: () => void;
}

const SessionContext = createContext<SessionContextType | undefined>(undefined);

export const SessionProvider: React.FC<{children: React.ReactNode}> = ({ children }) => {
  const [terminal, setTerminal] = useState<Terminal | null>(() => {
    const saved = localStorage.getItem('pos_terminal');
    return saved ? JSON.parse(saved) : null;
  });

  const [equipo, setEquipo] = useState<Equipo | null>(() => {
    const saved = localStorage.getItem('pos_equipo');
    return saved ? JSON.parse(saved) : null;
  });

  const setSessionData = useCallback((newTerminal: Terminal, newEquipo: Equipo) => {
    setTerminal(newTerminal);
    setEquipo(newEquipo);
    localStorage.setItem('pos_terminal', JSON.stringify(newTerminal));
    localStorage.setItem('pos_equipo', JSON.stringify(newEquipo));
  }, []);

  const clearSession = useCallback(() => {
    setTerminal(null);
    setEquipo(null);
    localStorage.removeItem('pos_terminal');
    localStorage.removeItem('pos_equipo');
  }, []);

  const value = useMemo(() => ({ terminal, equipo, setSessionData, clearSession }), [terminal, equipo, setSessionData, clearSession]);

  return (
    <SessionContext.Provider value={value}>
      {children}
    </SessionContext.Provider>
  );
};

export const useSession = () => {
  const context = useContext(SessionContext);
  if (context === undefined) {
    throw new Error('useSession debe ser usado dentro de un SessionProvider');
  }
  return context;
};
